import { useState, useEffect } from 'react';
import {
  fetchShippingSettings,
  fetchTermsSettings,
  fetchReturnsSettings,
  fetchLegalSettings,
  getFooterLinksSettingsFromWindow,
  formatTextWithBreaks
} from '../api/woocommerce';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

// Generic Footer Links Page Component
const FooterLinksPage = ({ pageType, title, fetchFunction }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentLanguage } = useLanguage();
const LoadingSkeleton = () => (
  <div className="px-2">
    <div className="h-8 bg-[#BDBDBD] animate-pulse mb-4 w-1/3"></div>
    <div className="mt-[49px]">
      <div className="h-4 bg-[#BDBDBD] animate-pulse mb-2 w-1/4"></div>
      <div className="h-4 bg-[#BDBDBD] animate-pulse mb-4 w-full"></div>
      <div className="h-4 bg-[#BDBDBD] animate-pulse mb-4 w-3/4"></div>
      <div className="h-4 bg-[#BDBDBD] animate-pulse mb-8 w-1/2"></div>
      
      <div className="h-4 bg-[#BDBDBD] animate-pulse mb-2 w-1/4"></div>
      <div className="h-4 bg-[#BDBDBD] animate-pulse mb-4 w-full"></div>
      <div className="h-4 bg-[#BDBDBD] animate-pulse mb-4 w-5/6"></div>
    </div>
  </div>
);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to get settings from window object first (faster)
        const windowSettings = getFooterLinksSettingsFromWindow();
        if (windowSettings && windowSettings[pageType]) {
          const pageSettings = windowSettings[pageType];
          if (pageSettings.enabled) {
            const sections = currentLanguage === 'mk' ?
              pageSettings.sections.mk :
              pageSettings.sections.en;
            setContent(sections);
          }
          setLoading(false);
          return;
        }

        // Fallback to API call
        const data = await fetchFunction(currentLanguage);
        if (data.enabled) {
          setContent(data.current_sections);
        }
      } catch (err) {
        console.error(`Error loading ${pageType} content:`, err);
        setError(`Failed to load ${title.toLowerCase()} content`);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [pageType, title, fetchFunction, currentLanguage]);
if (loading) {
    return <LoadingSkeleton />;
  }

  if (error || !content) {
    return (
      <div className="px-2 pt-16">
        <hr className="border-t border-black" />
        <div className="py-8 text-center">
          
        </div>
      </div>
    );
  }

  return (
    <div className="px-2">

      {/* Header */}
      <div>
        <h1 className="text-26 font-light">{title}</h1>
      </div>

      {/* Content Sections */}
      <div className="mt-[49px]">
        {content.map((section, index) => (
          <div key={index} className={index === content.length - 1 ? 'pb-64' : 'pb-4'}>
            {section.title && (
              <h2 className="text-14 font-medium">{section.title}</h2>
            )}
            {section.content && (
              <div
                className="text-14 font-light"
                dangerouslySetInnerHTML={{ __html: formatTextWithBreaks(section.content) }}
              />
            )}
          </div>
        ))}
      </div>


    </div>
  );
};

// Shipping Page Component
export const ShippingPage = () => {
    const { t } = useTranslation();

  return (
    <FooterLinksPage
      pageType="shipping"
      title={t('footer.shipping')}
      fetchFunction={fetchShippingSettings}
    />
  );
};

// Terms of Use Page Component
export const TermsPage = () => {
    const { t } = useTranslation();

  return (
    <FooterLinksPage
      pageType="terms"
      title={t('footer.terms')}
      fetchFunction={fetchTermsSettings}
    />
  );
};

// Returns & Exchanges Page Component
export const ReturnsPage = () => {
    const { t } = useTranslation();
  
  return (
    <FooterLinksPage
      pageType="returns"
      title={t('footer.returns')}
      fetchFunction={fetchReturnsSettings}
    />
  );
};

// Legal Page Component
export const LegalPage = () => {
    const { t } = useTranslation();

  return (
    <FooterLinksPage
      pageType="legal"
      title={t('footer.legal')}
      fetchFunction={fetchLegalSettings}
    />
  );
};
export default FooterLinksPage;